import { type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Button } from '../ui/Button';

/*
 * Курсорна пагінація API ({ data, nextCursor }): перша сторінка одразу, наступні — кнопкою «Показати ще».
 * Ключ запиту — префікс модуля (['legal', 'matters', фільтри]) для точкової інвалідації.
 */
export function useCursorQuery<T>(
  key: QueryKey,
  fetchPage: (cursor: string | undefined) => Promise<{ data: T[]; nextCursor: string | null }>,
  opts: { enabled?: boolean; staleTime?: number } = {},
) {
  const q = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime ?? 30_000,
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    items,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error,
    refetch: q.refetch,
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

/** Кнопка «Показати ще» під переліком (курсорна пагінація). */
export function LoadMore({ hasMore, loading, onClick, shown }: { hasMore: boolean; loading?: boolean; onClick: () => void; shown?: number }) {
  if (!hasMore) return null;
  return (
    <div className="flex items-center justify-center gap-3 py-1">
      {shown !== undefined && <span className="tnum text-meta text-fg-3">Показано {shown}</span>}
      <Button size="sm" onClick={onClick} loading={loading}>
        Показати ще
      </Button>
    </div>
  );
}
